import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { Typography } from '@mui/material';
import { Plus } from 'lucide-react';

import type { UserCard_UserFragment } from '@graphql/__generated__/graphql';
import { CardHeaderIconActions } from '@modules/_shared/components/CardHeaderIconActions';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorModal } from '@modules/_shared/components/MutationErrorModal';
import {
  EditableUserCard,
  type TEditUserInputProps,
  UserActionCard,
} from '@modules/_shared/components/UserActionCard';
import { UserCardSkeleton } from '@modules/_shared/components/UserCard';
import { useConfirmAgentChange } from '@modules/_shared/hooks/useConfirmAgentChange';
import { useLocale } from '@shared/hooks/locale';

interface ListingAgentCardProps {
  user?: UserCard_UserFragment | null;
  secondaryText?: React.ReactNode;
  isLoading?: boolean;
  openedDefault?: boolean;
  readOnly?: boolean;
  editUserInputProps?: TEditUserInputProps;
  onUserChanged: (
    userId: string | null,
    onError: (err: ErrorLike) => void,
  ) => Promise<any>;
}

export const ListingAgentCardSkeleton: React.FC<{
  openedDefault?: boolean;
}> = ({ openedDefault = false }) => {
  const { t } = useLocale();

  return (
    <ListingAccordion title={t('Agent')} defaultExpanded={openedDefault}>
      <UserCardSkeleton condensed hasSecondaryText />
    </ListingAccordion>
  );
};

export const ListingAgentCard: React.FC<ListingAgentCardProps> = ({
  user,
  secondaryText,
  onUserChanged,
  isLoading = false,
  openedDefault = false,
  readOnly = false,
  editUserInputProps = {},
}) => {
  const { t } = useLocale();
  const [displayAddAgent, setDisplayAddAgent] = useState(false);
  const [toggleError, setToggleError] = useState<null | ErrorLike>(null);
  const { confirm, dialog } = useConfirmAgentChange();

  if (isLoading) {
    return <ListingAgentCardSkeleton openedDefault={openedDefault} />;
  }

  return (
    <>
      <ListingAccordion
        title={t('Agent')}
        defaultExpanded={openedDefault}
        headerAction={
          readOnly || user != null ? undefined : (
            <CardHeaderIconActions
              actions={[
                {
                  label: t('Add agent'),
                  icon: <Plus size={16} />,
                  onClick: () => setDisplayAddAgent(true),
                  disabled: displayAddAgent,
                },
              ]}
            />
          )
        }
      >
        {user != null && (
          <UserActionCard
            user={user}
            secondaryText={secondaryText}
            readOnly={readOnly}
            onUserChanged={async (userId, onError) => {
              if (await confirm()) {
                await onUserChanged(userId, onError);
              }
            }}
            editUserInputProps={editUserInputProps}
          />
        )}

        {user == null && !displayAddAgent && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('No agent assigned')}
          </Typography>
        )}

        {!readOnly && user == null && displayAddAgent && (
          <EditableUserCard
            isEditing
            onChange={async userId => {
              await onUserChanged(userId, setToggleError);
              setDisplayAddAgent(false);
            }}
            onCancel={() => setDisplayAddAgent(false)}
            {...editUserInputProps}
          />
        )}
      </ListingAccordion>
      <MutationErrorModal
        error={toggleError}
        onClose={() => setToggleError(null)}
      />
      {dialog}
    </>
  );
};
